import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { post, request } from './request';

export type PortoBiometryChannel = 'SMS' | 'EMAIL';

export type PortoBiometryHistoryEntry = {
  action: string;
  requester: string | null;
  deliveries: Array<{ channel: string; recipient: string }>;
  date: string | null;
  rawDate: string | null;
  processing: 'Sucesso' | 'Erro' | 'Alerta' | string | null;
  message: string | null;
};

export type PortoBiometryLastSend = {
  action: string;
  channel: string;
  recipient: string;
  sentAt: string | null;
};

export type PortoBiometryResponse = {
  analysisId: string;
  url: string | null;
  generatedAt: string | null;
  lastSend: PortoBiometryLastSend | null;
  suggestedRecipients: { sms: string | null; email: string | null };
  nextSendAvailableAt: string | null;
  history: PortoBiometryHistoryEntry[];
};

export type PortoBiometrySendResult = PortoBiometryResponse & {
  sent: {
    action: string;
    channel: PortoBiometryChannel;
    recipient: string;
    message: string | null;
  };
};

export type PortoBiometrySendPayload = {
  channel: PortoBiometryChannel;
  recipient: string;
};

export type PortoBiometrySendError = {
  statusCode?: number;
  code?: string;
  message?: string | string[];
  nextSendAvailableAt?: string;
};

/** Login no Corretor Online em andamento no backend; vale tentar de novo em instantes. */
export const PORTO_SESSION_RENEWING = 'PORTO_SESSION_RENEWING';
const RENEWING_RETRY_MS = 8 * 1000;

const portoBiometryKey = (analysisId: string) => ['porto-biometry', analysisId];

export function isPortoSessionRenewing(error: unknown): boolean {
  return (error as PortoBiometrySendError | null)?.code === PORTO_SESSION_RENEWING;
}

// Usa `request` direto porque `get` descarta o `code` do corpo de erro.
export async function fetchPortoBiometry(
  analysisId: string
): Promise<PortoBiometryResponse> {
  const response = await request(
    `/bond-insurance-analysis/${analysisId}/porto-biometry`,
    'GET'
  );
  if (!response) {
    throw { message: 'Não foi possível obter resposta do servidor.' };
  }
  if (response.status >= 400) {
    throw response.data ?? { message: 'Erro ao consultar a biometria.' };
  }
  return response.data;
}

export async function sendPortoBiometry(
  analysisId: string,
  payload: PortoBiometrySendPayload
): Promise<PortoBiometrySendResult> {
  return post(`/bond-insurance-analysis/${analysisId}/porto-biometry/send`, payload);
}

export function getPortoBiometryErrorMessage(error: unknown): string {
  const fallback = 'Não foi possível concluir a solicitação. Tente novamente.';
  if (!error) {
    return fallback;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  const message = (error as PortoBiometrySendError).message;
  if (Array.isArray(message)) {
    return message.join(' ');
  }
  return message || fallback;
}

export function usePortoBiometry(analysisId: string, enabled: boolean) {
  return useQuery<PortoBiometryResponse, PortoBiometrySendError>(
    portoBiometryKey(analysisId),
    () => fetchPortoBiometry(analysisId),
    {
      enabled: enabled && Boolean(analysisId),
      staleTime: 30 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: (_data, query) =>
        isPortoSessionRenewing(query.state.error) ? RENEWING_RETRY_MS : false
    }
  );
}

export function useSendPortoBiometry(analysisId: string) {
  const queryClient = useQueryClient();
  return useMutation<PortoBiometrySendResult, PortoBiometrySendError, PortoBiometrySendPayload>(
    payload => sendPortoBiometry(analysisId, payload),
    {
      onSuccess: data => {
        queryClient.setQueryData(portoBiometryKey(analysisId), data);
      }
    }
  );
}
